import {
  Alert,
  Box,
  Card,
  Grid,
  Icon,
  IconButton,
  Snackbar,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  useTheme
} from '@mui/material';
import { H6 } from 'app/components/Typography';
import React from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  genrateVendorReceiptPdf,
  getAllVendorReceiptData
} from 'app/contexts/reducers/transaction/vendorReceiptSlice';
import SearchAccount from './SearchAccount';
import FilterOrderByMonth from './FilterReceiptByMonth';
import FilterStatus from './FilterStatus';
import UpdateVendorReceiptDialog from './UpdateVendorReceiptDialog';
import DeleteVendorReceipt from './DeleteVendorReceipt';

const CardHeader = styled(Box)(() => ({
  display: 'flex',
  paddingLeft: '24px',
  paddingRight: '24px',
  marginBottom: '12px',
  alignItems: 'center',
  justifyContent: 'space-between'
}));

const Title = styled('span')(() => ({
  fontSize: '1rem',
  fontWeight: '500',
  textTransform: 'capitalize'
}));

const ProductTable = styled(Table)(() => ({
  minWidth: 400,
  whiteSpace: 'pre',
  '& small': {
    width: 50,
    height: 15,
    borderRadius: 500,
    boxShadow: '0 0 2px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.24)'
  },
  '& td': { borderBottom: 'none' },
  '& td:first-of-type': { paddingLeft: '16px !important' }
}));

const Small = styled('small')(({ bgcolor }) => ({
  width: 50,
  height: 15,
  color: '#fff',
  padding: '2px 8px',
  borderRadius: '4px',
  overflow: 'hidden',
  background: bgcolor,
  boxShadow: '0 0 2px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.24)'
}));

const VendorReceiptTable = ({ apiCall, handleApiCall }) => {
  const { palette } = useTheme();
  const bgSuccess = palette.success.main;
  const bgSecondary = palette.secondary.main;
  const { isAllVendorReceiptsSuccess, allVendorReceiptData } = useSelector(
    (state) => state.vendorReceipt
  );
  const [allVendorReceiptsData, setAllVendorReceiptsData] = useState([]);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [receiptId, setReceiptId] = useState(null);
  const [deleteReceiptId, setDeleteReceiptId] = useState(null);
  const [errorOpen, setErrorOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState();
  const [error, setError] = useState();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAllVendorReceiptData());
  }, [dispatch, apiCall]);

  useEffect(() => {
    if (isAllVendorReceiptsSuccess) {
      if (Number(allVendorReceiptData.data.code) === 200) {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const vendorReceiptWithCurrentMonth = allVendorReceiptData.data.data.filter((receipt) => {
          const receiptDate = new Date(receipt.date);
          const receiptMonth = receiptDate.getMonth() + 1; // Adding 1 since getMonth() returns a zero-based index
          return receiptMonth === currentMonth;
        });
        setAllVendorReceiptsData(vendorReceiptWithCurrentMonth);
      } else {
        setAllVendorReceiptsData([]);
      }
    }
  }, [isAllVendorReceiptsSuccess, allVendorReceiptData]);
  const convertDate = (isoDateString) => {
    const date = new Date(isoDateString);

    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    };

    const formattedDate = date.toLocaleString('en-GB', options).replace(/\//g, '-');
    return formattedDate;
  };
  function handleUpdateClose() {
    setUpdateOpen(false);
  }
  function handleDeleteClose() {
    setDeleteOpen(false);
  }
  function handleClickSuccess(message) {
    setErrorOpen(false);
    setSuccessMessage(message);
    setSuccessOpen(true);
  }
  function handleClickError() {
    setSuccessOpen(false);
    setErrorOpen(true);
  }
  function handleSnakeBarClose(_, reason) {
    if (reason === 'clickaway') {
      return;
    }
    setSuccessOpen(false);
    setErrorOpen(false);
  }
  function calculateTotalAndDiscount(allVendorReceiptsData) {
    let amount = 0;
    let discountAmount = 0;
    for (let i = 0; i < allVendorReceiptsData.length; i++) {
      const receipt = allVendorReceiptsData[i];

      // Add the amount to the total
      amount += parseInt(receipt.amount);

      // Add the discount_amount to the discountAmount
      discountAmount += parseInt(receipt.discount_amount);
    }

    return { amount, discountAmount };
  }

  return (
    <React.Fragment>
      <Card elevation={3} sx={{ pt: '20px', mb: 3 }}>
        <CardHeader>
          <Grid container justifyContent='space-between'>
            <Grid item>
              <Title>Vendor Receipt Table</Title>
            </Grid>
            <Grid item>
              <Grid container spacing={1}>
                <Grid item>
                  <SearchAccount setAllVendorReceiptsData={setAllVendorReceiptsData} />
                </Grid>
                <Grid item>
                  <FilterOrderByMonth setAllVendorReceiptsData={setAllVendorReceiptsData} />
                </Grid>
                <Grid item>
                  <FilterStatus setAllVendorReceiptsData={setAllVendorReceiptsData} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardHeader>

        <Box overflow='auto'>
          <ProductTable>
            <TableHead sx={{ backgroundColor: 'rgb(34,41,67)' }}>
              <TableRow>
                <TableCell align='center' sx={{ px: 2, fontWeight: 'bold', color: '#FFFFFF' }}>
                  Index
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ px: 0, fontWeight: 'bold', color: '#FFFFFF' }}
                  colSpan={2}
                >
                  Vendor Name
                </TableCell>
                <TableCell align='left' sx={{ px: 3, fontWeight: 'bold', color: '#FFFFFF' }}>
                  Amount
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ px: 1, fontWeight: 'bold', color: '#FFFFFF' }}
                  colSpan={2}
                >
                  Discount Amount
                </TableCell>
                <TableCell align='center' sx={{ px: 0, fontWeight: 'bold', color: '#FFFFFF' }}>
                  Payment Type
                </TableCell>
                <TableCell align='center' sx={{ px: 0, fontWeight: 'bold', color: '#FFFFFF' }}>
                  Book Number
                </TableCell>

                <TableCell align='center' sx={{ px: 0, fontWeight: 'bold', color: '#FFFFFF' }}>
                  Date
                </TableCell>

                <TableCell
                  align='center'
                  sx={{ px: 5, fontWeight: 'bold', color: '#FFFFFF' }}
                  colSpan={2}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {allVendorReceiptsData && allVendorReceiptsData.length > 0 ? (
                allVendorReceiptsData.map((vendorReceiptDetails, index) => (
                  <TableRow key={index} hover>
                    <TableCell align='center' sx={{ px: 0, textTransform: 'capitalize' }}>
                      <H6 sx={{ px: 2 }}>{index + 1}</H6>
                    </TableCell>
                    <TableCell
                      align='center'
                      sx={{ px: 0, textTransform: 'capitalize', fontWeight: 'bold' }}
                      colSpan={2}
                    >
                      {vendorReceiptDetails.vendor_name}
                    </TableCell>
                    <TableCell
                      align='left'
                      sx={{ px: 2, fontWeight: 'bold', textTransform: 'capitalize' }}
                    >
                      {vendorReceiptDetails.amount}
                    </TableCell>
                    <TableCell align='center' sx={{ px: 2, fontWeight: 'bold' }} colSpan={2}>
                      {vendorReceiptDetails.discount_amount}
                    </TableCell>

                    <TableCell align='center' sx={{ px: 2 }}>
                      {vendorReceiptDetails.payment_type === 'CASH' && (
                        <Small bgcolor={bgSecondary}>CASH</Small>
                      )}
                      {vendorReceiptDetails.payment_type === 'BANK' && (
                        <Small bgcolor={bgSuccess}>BANK</Small>
                      )}
                    </TableCell>
                    <TableCell align='center' sx={{ px: 2 }}>
                      {vendorReceiptDetails.book_number}
                    </TableCell>

                    <TableCell align='center' sx={{ px: 0 }}>
                      {convertDate(vendorReceiptDetails.date)}
                    </TableCell>

                    <TableCell align='center' sx={{ px: 2 }} colSpan={2}>
                      <Tooltip title='Print'>
                        <IconButton
                          onClick={() => {
                            const invoiceId = vendorReceiptDetails.id;
                            dispatch(genrateVendorReceiptPdf({ invoiceId }));
                          }}
                        >
                          <Icon color='primary'>print</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Edit'>
                        <IconButton
                          onClick={() => {
                            setReceiptId(vendorReceiptDetails.id);
                            setUpdateOpen(true);
                          }}
                        >
                          <Icon color='primary'>edit</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Delete'>
                        <IconButton
                          onClick={() => {
                            setDeleteReceiptId(vendorReceiptDetails.id);
                            setDeleteOpen(true);
                          }}
                        >
                          <Icon color='error'>close</Icon>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    align='center'
                    sx={{ color: 'rgb(34,41,67)', fontWeight: 'bold' }}
                  >
                    No Receipt Data Found
                  </TableCell>
                </TableRow>
              )}
              <TableRow sx={{ backgroundColor: 'rgb(34,41,67)' }}>
                <TableCell align='center' sx={{ px: 0, textTransform: 'capitalize' }}></TableCell>
                <TableCell
                  align='center'
                  sx={{ px: 0, textTransform: 'capitalize', fontWeight: 'bold' }}
                  colSpan={2}
                ></TableCell>
                <TableCell
                  align='left'
                  sx={{ px: 2, fontWeight: 'bold', color: 'white', textTransform: 'capitalize' }}
                >
                  {calculateTotalAndDiscount(allVendorReceiptsData).amount}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ px: 2, color: 'white', fontWeight: 'bold' }}
                  colSpan={2}
                >
                  {calculateTotalAndDiscount(allVendorReceiptsData).discountAmount}
                </TableCell>
                <TableCell align='center' sx={{ px: 2 }}></TableCell>
                <TableCell align='center' sx={{ px: 2 }}></TableCell>
                <TableCell align='center' sx={{ px: 0 }}></TableCell>
                <TableCell align='center' sx={{ px: 2 }} colSpan={2}></TableCell>
              </TableRow>
            </TableBody>
          </ProductTable>
        </Box>
      </Card>
      {/* Alert */}
      <Snackbar open={successOpen} autoHideDuration={3000} onClose={handleSnakeBarClose}>
        <Alert
          onClose={handleSnakeBarClose}
          severity='success'
          sx={{ width: '100%' }}
          variant='filled'
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={errorOpen} autoHideDuration={3000} onClose={handleSnakeBarClose}>
        <Alert
          onClose={handleSnakeBarClose}
          severity='error'
          sx={{ width: '100%' }}
          variant='filled'
        >
          {error?.payload}
        </Alert>
      </Snackbar>
      {/* Update-Customer-Dialog */}
      <UpdateVendorReceiptDialog
        open={updateOpen}
        onClose={handleUpdateClose}
        receiptId={receiptId}
        handleApiCall={handleApiCall}
        handleClickSuccess={handleClickSuccess}
        setError={setError}
        handleClickError={handleClickError}
      />
      {/* Start-Delete-Account */}
      <DeleteVendorReceipt
        open={deleteOpen}
        onClose={handleDeleteClose}
        receiptId={deleteReceiptId}
        handleApiCall={handleApiCall}
        handleClickSuccess={handleClickSuccess}
        handleClickError={handleClickError}
        setError={setError}
      />
    </React.Fragment>
  );
};

export default VendorReceiptTable;
